import { useInfiniteQuery, useMutation, useQuery } from 'react-query';

import { useServiceError } from '@/hooks';

import {
  ChangeWorkOrderItemDiscountRequest,
  CreateWorkOrderItemRequest,
  DeleteWorkOrderItemRequest,
  WorkOrderItemActionListRequest,
  WorkOrderItemHistoryRequest,
  WorkOrderItemType,
  WorkOrderPriceDiferenceListRequest,
} from './WorkOrdersItems.types';

import {
  listWorkOrderItems,
  deleteWorkOrderItem,
  createWorkOrderItem,
  updateWorkOrderItem,
  getWorkOrderItem,
  changeWorkOrderItemDiscount,
  createWorkOrderItemList,
  changeWorkOrderItemTag,
  changeWorkOrderItemRestore,
  listWorkOrderItemHistory,
  listWorkOrderPriceDiference,
  workOrderItemsSwapOperationPart,
  workOrderItemBenefitsCreate,
  workOrderItemBenefitsRemove,
  workOrderItemRefusePublic,
  workOrderHasRemovedItems,
  workOrderItemBatchRemove,
  workOrderItemActionList,
} from './WorkOrdersItems.service';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import { workorderDicionaryError } from '..';
import { AxiosError } from 'axios';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { QUERY_ID } from '../RequestPurchase/RequestPurchase.hooks';
import { showItemsErrorModal } from '@/utils/functions/showItemsErrorModal';
import { mapWorkOrderItemFieldErrors } from '@/utils/functions/buildItemErrorsList';
import { PECAAI_BASKET_DETAIL } from '../../pecaAi/basket/pecaAiBasket.hooks';

export const WORKORDER_ITEMS_QUERY_KEY = 'workorders/complements/items';
export const WORKORDER_HAS_REMOVED_ITENS_KEY =
  'workorders/complements/items/has-removed-itens';
export const WORKORDER_ITEM_ACTION_LIST = 'workorders/items/actions';

export type ErrorItemQuotation = AxiosError<{
  message: string;
  name: string;
  description: string;
  fieldErrors: Record<string, string[]>;
}>;

export function useWorkOrderItems(
  complementId?: number,
  showRemoved = false,
  itemType?: WorkOrderItemType,
  hasCache?: boolean,
) {
  const cache = hasCache
    ? queryClient.getQueryData([
        WORKORDER_ITEMS_QUERY_KEY,
        { complementId, showRemoved, itemType },
      ])
    : undefined;

  return useQuery(
    [WORKORDER_ITEMS_QUERY_KEY, { complementId, showRemoved, itemType }],
    () => {
      return listWorkOrderItems(complementId, showRemoved, itemType);
    },
    {
      enabled: !!complementId && !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderItemsVerify(
  complementId?: number,
  showRemoved = false,
  config?: {
    itemType?: WorkOrderItemType;
    enabled?: boolean;
  },
) {
  const query = useQuery(
    [`${WORKORDER_ITEMS_QUERY_KEY}/verify`],
    () => {
      return listWorkOrderItems(complementId, showRemoved, config?.itemType);
    },
    {
      enabled:
        typeof config?.enabled === 'boolean'
          ? config?.enabled && !!complementId
          : !!complementId,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useWorkOrderItem(id: number) {
  const query = useQuery([`${WORKORDER_ITEMS_QUERY_KEY}/detail`, id], () =>
    getWorkOrderItem(id),
  );

  useServiceError(query.error);

  return query;
}

export function useCreateWorkOrderItem() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: CreateWorkOrderItemRequest) => createWorkOrderItem(obj),
    {
      async onSuccess() {
        queryChanel({ resetQueries: [[WORKORDER_ITEMS_QUERY_KEY]] });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useCreateWorkOrderItemList(workOrderId?: number) {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(createWorkOrderItemList, {
    async onSuccess(_, vars) {
      notification.success({
        message: 'Itens cadastrados',
        description: 'Os itens foram cadastrados com sucesso',
      });

      queryChanel({
        invalidateQueries: [
          ['workOrders', workOrderId],
          [`${WORKORDER_ITEMS_QUERY_KEY}/verify`],
        ],
        resetQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          ['workOrders/complements', vars.complementId],
        ],
      });
    },

    onError(error) {
      const axiosError = error as ErrorItemQuotation;

      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => {
            if (axiosError.response?.data.fieldErrors?.Discount) {
              return axiosError.response.data.fieldErrors.Discount[0];
            }

            return workorderDicionaryError[err] || err;
          }),
      });
    },
  });

  return mutation;
}

export function useUpdateWorkOrderItem(
  workOrderId?: number,
  complementId?: number,
) {
  const queryChanel = useQueryChanel();

  return useMutation(updateWorkOrderItem, {
    async onSuccess(_, vars) {
      notification.success({
        message: 'Item atualizado',
        description: `O item "${vars.itemName}" foi atualizado com sucesso`,
      });

      queryChanel({
        invalidateQueries: [[`${WORKORDER_ITEMS_QUERY_KEY}/verify`]],
        resetQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          ['workOrders/tracking/calendar/complement', complementId],
          [QUERY_ID],
        ],
      });
    },

    onError(error) {
      const axiosError = error as ErrorItemQuotation;

      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => {
            if (
              err === 'workorder.errors.workorder_there_is_a_linked_purchase'
            ) {
              return 'Existe compra vinculada, para diminuir a quantidade, deve ser alterada na compra gerada.';
            }
            if (
              err === 'stock.errors.part_in_quotation' ||
              err === 'workorder.errors.workorder_there_is_purchase'
            ) {
              const key = 'workorder.errors.workorder_there_is_purchase';
              if (
                axiosError.isAxiosError &&
                Object.keys(
                  axiosError.response?.data.fieldErrors || {},
                ).includes(key)
              ) {
                return `A Quantidade não pode ser editada, pois existe uma compra de código ${replaceCode(
                  axiosError.response?.data.fieldErrors[key][0],
                )} referente a ele.`;
              }
              return 'Existe cotação vinculada, para diminuir a quantidade, deve ser alterada na cotação gerada.';
            }
            return workorderDicionaryError[err] || err;
          }),
      });
    },
  });
}

function replaceCode(text?: string) {
  return text?.replace(/[^\d]+/, '');
}

export function useDeleteWorkOrderItem(hiddenSucces = false) {
  const queryChanel = useQueryChanel();

  return useMutation(
    (obj: DeleteWorkOrderItemRequest) => deleteWorkOrderItem(obj),
    {
      async onSuccess(_, vars) {
        if (!hiddenSucces) {
          notification.success({
            message: 'Item removido',
            description: 'O item selecionado foi removido com sucesso!',
          });
        }

        if (vars.hasPecaAi) {
          queryChanel({ invalidateQueries: [[PECAAI_BASKET_DETAIL]] });
        }
      },
      onError(error) {
        const axiosError = error as ErrorItemQuotation;

        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => {
              if (
                err === 'stock.errors.part_in_quotation' ||
                err === 'workorder.errors.workorder_there_is_purchase'
              ) {
                const key = 'workorder.errors.workorder_there_is_purchase';
                if (
                  axiosError.isAxiosError &&
                  Object.keys(
                    axiosError.response?.data.fieldErrors || {},
                  ).includes(key)
                ) {
                  return `Este item não pode ser excluído, pois existe uma compra de código ${replaceCode(
                    axiosError.response?.data.fieldErrors[key][0],
                  )} referente a ele.`;
                }
              }
              return workorderDicionaryError[err] || err;
            }),
        });
      },
    },
  );
}

export function useChangeWorkOrderItemDiscount() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: ChangeWorkOrderItemDiscountRequest) =>
      changeWorkOrderItemDiscount(obj),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'workOrders.details.items.changeDiscount.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'workOrders.details.items.changeDiscount.notifications.success.description',
          }),
        });

        queryChanel({
          invalidateQueries: [[`${WORKORDER_ITEMS_QUERY_KEY}/verify`]],
          resetQueries: [[WORKORDER_ITEMS_QUERY_KEY]],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useChangeWorkOrderItemTag() {
  const queryChanel = useQueryChanel();

  return useMutation(changeWorkOrderItemTag, {
    onSuccess() {
      notification.success({
        message: 'Situação alterada',
        description: 'A situação do item foi alterada com sucesso!',
      });

      queryChanel({
        invalidateQueries: [[`${WORKORDER_ITEMS_QUERY_KEY}/verify`]],
        resetQueries: [[WORKORDER_ITEMS_QUERY_KEY]],
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useChangeWorkOrderItemRestore() {
  return useMutation(changeWorkOrderItemRestore, {
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes(
              'workorder.errors.workorder_item_has_already_been_deleted',
            )
              ? 'O item da ordem de serviço já foi excluído'
              : err,
          ),
      });
    },
  });
}

export function useWorkOrderItemHistory(params?: WorkOrderItemHistoryRequest) {
  return useQuery(
    [`${WORKORDER_ITEMS_QUERY_KEY}/history`, params],
    () => listWorkOrderItemHistory(params),
    {
      enabled:
        !!params?.complementId &&
        !!params?.partId &&
        !!params?.from &&
        !!params?.to,

      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderPriceDiferenceList(
  params?: WorkOrderPriceDiferenceListRequest,
  enabled?: boolean,
) {
  return useQuery(
    [`${WORKORDER_ITEMS_QUERY_KEY}/history`, params],
    () => listWorkOrderPriceDiference(params),
    {
      enabled: !!params?.complementId && !!params.companyId && enabled,

      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderItemsSwapOperationPart() {
  const queryChanel = useQueryChanel();

  return useMutation(workOrderItemsSwapOperationPart, {
    onSuccess() {
      notification.success({ message: 'Item atualizado com sucesso' });
      queryChanel({ invalidateQueries: [WORKORDER_ITEMS_QUERY_KEY] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderItemBenefitsCreate() {
  const queryChanel = useQueryChanel();

  return useMutation(workOrderItemBenefitsCreate, {
    onSuccess() {
      notification.success({
        message: 'Benefícios adicionados ao complemento',
      });
      queryChanel({ invalidateQueries: [WORKORDER_ITEMS_QUERY_KEY] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderItemBenefitsRemove() {
  const queryChanel = useQueryChanel();

  return useMutation(workOrderItemBenefitsRemove, {
    onSuccess() {
      notification.success({
        message: 'Benefício removido do complemento',
      });
      queryChanel({ resetQueries: [WORKORDER_ITEMS_QUERY_KEY] });
    },
    onError(error, variables) {
      const axiosError = error as ErrorItemQuotation;
      const errorsList = mapWorkOrderItemFieldErrors(axiosError, variables);

      if (!!errorsList.length) {
        showItemsErrorModal(errorsList);
      } else {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => workorderDicionaryError[err] ?? err),
        });
      }

      queryChanel({
        invalidateQueries: [[`${WORKORDER_ITEMS_QUERY_KEY}/verify`]],
        resetQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          [
            WORKORDER_HAS_REMOVED_ITENS_KEY,
            { complementId: variables.complementId },
          ],
        ],
      });
    },
  });
}

export function useWorkOrderItemRefusePublic() {
  const queryChanel = useQueryChanel();

  return useMutation(workOrderItemRefusePublic, {
    onSuccess() {
      notification.success({
        message: 'Item do orçamento recusado com sucesso',
      });
      queryChanel({ resetQueries: ['detail-workOrder-qrCode'] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderHasRemovedItems(complementId?: number) {
  const cache = queryClient.getQueryData([
    WORKORDER_HAS_REMOVED_ITENS_KEY,
    { complementId },
  ]);

  return useQuery(
    [WORKORDER_HAS_REMOVED_ITENS_KEY, { complementId }],
    () => workOrderHasRemovedItems(complementId),
    { enabled: !!complementId && !cache },
  );
}

export function useWorkOrderItemBatchRemove() {
  const queryChanel = useQueryChanel();

  return useMutation(workOrderItemBatchRemove, {
    async onSuccess(_, vars) {
      notification.success({
        message: 'Itens removidos',
        description: 'Os itens selecionados foram removidos com sucesso!',
      });

      queryChanel({
        invalidateQueries: [[`${WORKORDER_ITEMS_QUERY_KEY}/verify`]],
        resetQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          [
            WORKORDER_HAS_REMOVED_ITENS_KEY,
            { complementId: vars.complementId },
          ],
        ],
      });
    },

    onError(error, variables) {
      const axiosError = error as ErrorItemQuotation;
      const errorsList = mapWorkOrderItemFieldErrors(axiosError, variables);

      if (!!errorsList.length) {
        showItemsErrorModal(errorsList);
      } else {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => workorderDicionaryError[err] ?? err),
        });
      }

      queryChanel({
        invalidateQueries: [[`${WORKORDER_ITEMS_QUERY_KEY}/verify`]],
        resetQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          [
            WORKORDER_HAS_REMOVED_ITENS_KEY,
            { complementId: variables.complementId },
          ],
        ],
      });
    },
  });
}

export function useWorkOrderItemActionList(
  params: WorkOrderItemActionListRequest,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: [WORKORDER_ITEM_ACTION_LIST, params],
    queryFn: ({ pageParam }) =>
      workOrderItemActionList({ rows: 10, ...params, nextToken: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextToken,
    enabled: !!params.complementId && enabled,
  });
}
